import { parsePhoneNumberFromString, AsYouType } from "libphonenumber-js";

/**
 * Formats phone numbers based on specific rules:
 * - Numbers starting with +1 are formatted as "(123) 456-7891".
 * - Other numbers are formatted using their regional standards.
 * @param {string} inputNumber - The phone number entered by the user.
 * @returns {string} - Formatted phone number or error message.
 */
export function formatPhoneNumber(inputNumber) {
  try {
    // Parse the phone number without a default country
    let phoneNumber = parsePhoneNumberFromString(inputNumber);


    if (!phoneNumber) {
      
      phoneNumber = parsePhoneNumberFromString(inputNumber,'US')
      //console.log(phoneNumber,inputNumber)
      //throw new Error("Invalid phone number format");
     
    }

    // If the number starts with +1, format it as "(123) 456-7891"
    if (phoneNumber.countryCallingCode === "1") {
      const nationalNumber = phoneNumber.nationalNumber;
      return `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`;
    }

    // For other numbers, use the standard international format
    return phoneNumber.formatInternational();
  } catch (error) {
    return inputNumber;
  }
}

/**
 * Real-time formatting for input fields:
 * - Numbers starting with +1 are formatted as "(123) 456-7891".
 * - Other numbers are formatted as-you-type using the library.
 * @param {string} inputNumber - The phone number entered by the user.
 * @returns {string} - Real-time formatted phone number.
 */
export function formatPhoneNumberAsYouType(inputNumber) {
  // Skip auto-formatting for international numbers (those starting with +)
  if (inputNumber.startsWith("+")) {
    return inputNumber;
  }

  const formatter = new AsYouType();

  // First, format the input using the AsYouType formatter
  let formattedNumber = formatter.input(inputNumber);

  // Check if the number is from the US (+1) and is longer than 10 digits
  if ((inputNumber.startsWith("+1") || inputNumber.startsWith("1"))&& inputNumber.replace(/\D/g, "").length > 10) {
    const cleanInput = inputNumber.replace(/\D/g, ""); // Remove non-numeric characters
    // First digit stands alone, followed by (xxx) xxx-xxxx format
    formattedNumber = `${cleanInput.slice(0, 1)} (${cleanInput.slice(1, 4)}) ${cleanInput.slice(4, 7)}-${cleanInput.slice(7, cleanInput.length)}`;
  }
  
  // Check if it's a valid 10-digit phone number (local US number)
  else if (inputNumber.replace(/\D/g, "").length === 10) {
    const cleanInput = inputNumber.replace(/\D/g, ""); // Remove non-numeric characters
    formattedNumber = `(${cleanInput.slice(0, 3)}) ${cleanInput.slice(3, 6)}-${cleanInput.slice(6, 10)}`;
  }

  return formattedNumber ;
}

/**
 * Number to hand to create-call / Twilio from the dialer's free-text recipient field.
 * Mirrors the pre-lookup field, which stripped letters as you typed: input without
 * letters is returned unchanged; otherwise only digits (and a leading +) are kept.
 * Deliberately avoids libphonenumber so a parsing quirk can never block a call.
 * @param {string} input - Raw recipient field value.
 * @returns {string} - Dialable number, possibly empty.
 */
export function toDialableNumber(input) {
  const trimmed = (input || "").trim();
  if (!/[a-zA-Z]/.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  return trimmed.startsWith("+") ? `+${digits}` : digits;
}

/**
 * Label for phone number dropdown options. Shows "number - friendly name" only when
 * the friendly name exists and is different from the number; otherwise just the number.
 * @param {{ phoneNumber: string, friendlyName?: string }} option - Option with phoneNumber and optional friendlyName
 * @returns {string} - Display label for the option
 */
export function getPhoneOptionLabel(option) {
  if (!option?.phoneNumber) return "";
  const formatted = formatPhoneNumber(option.phoneNumber);
  const fn = (option.friendlyName || "").trim();
  const showFriendly = fn && fn !== formatted && fn !== option.phoneNumber;
  return showFriendly ? `${formatted} - ${option.friendlyName}` : formatted;
}
